/**
 * Signup form DOM chrome: labels, placeholders, progress badge, error box.
 */

import {
  getTaxIdInputMode,
  getTaxIdLabel,
  getTaxIdMaxLength,
  getTaxIdPlaceholder,
} from "../utils/tax-id.js";
import { formatAsYouType, getPhonePlaceholder } from "../utils/phone.js";
import { signupChromeForCountry } from "./self-serve-countries.js";

/**
 * @param {1|2} step
 * @param {number} [trialDays] 0 = no confirmed trial for the current currency
 */
export function setSignupProgressStep(step, trialDays = 0) {
  const badge = document.getElementById("signupProgressBadge");
  if (!badge) return;
  if (step !== 2) {
    badge.textContent = "Passo 1 de 2 — Dados da empresa";
    return;
  }
  badge.textContent =
    trialDays > 0
      ? "Passo 2 de 2 — Plano e cartão (sem cobrança hoje)"
      : "Passo 2 de 2 — Plano e pagamento";
}

/**
 * @param {string} message
 */
export function showFormError(message) {
  const errorBox = document.getElementById("signupError");
  if (!errorBox) return;
  errorBox.textContent = message || "Dados inválidos.";
  errorBox.classList.remove("hidden");
}

export function clearFormError() {
  const errorBox = document.getElementById("signupError");
  if (!errorBox) return;
  errorBox.classList.add("hidden");
  errorBox.textContent = "";
}

/**
 * @param {string} country
 * @param {number} trialDays
 */
function updateHowItWorksCopy(country, trialDays) {
  const list = document.getElementById("signupHowItWorksList");
  if (!list) return;
  const taxLabel = getTaxIdLabel(signupChromeForCountry(country).taxIdType);
  const steps = [
    `Informe o ${taxLabel} — preenchemos parte das informações automaticamente quando disponível.`,
    trialDays > 0
      ? `Escolha o plano e cadastre o cartão: nada é cobrado hoje e a primeira cobrança só acontece depois de ${trialDays} dias.`
      : "Escolha o plano e finalize o pagamento em ambiente seguro.",
    "Acesse com o código enviado ao e-mail informado e defina sua senha.",
  ];
  list.innerHTML = steps
    .map(
      (text, i) =>
        `<li class="flex gap-3"><span class="font-bold text-primary">${i + 1}.</span>${text}</li>`
    )
    .join("");
}

/**
 * Apply BrasilAPI company enrichment into email/phone fields.
 * @param {Record<string, unknown>|null|undefined} data
 * @param {string} phoneRegion
 */
export function applyBrasilApiCompanyData(data, phoneRegion) {
  if (!data || typeof data !== "object") return;
  const emailEl = document.getElementById("contactEmail");
  const phoneEl = document.getElementById("contactPhone");
  const mail = String(data.email || "").trim();
  if (emailEl && mail) emailEl.value = mail;
  const ddd = String(data.ddd_telefone_1 || "").replace(/\D/g, "");
  const tel = String(data.telefone_1 || "").replace(/\D/g, "");
  if (phoneEl && ddd && tel) {
    phoneEl.value = formatAsYouType(ddd + tel, phoneRegion);
  }
}

/**
 * @param {ReturnType<typeof signupChromeForCountry>} chrome
 * @param {number} [trialDays] 0 = no confirmed trial for the current currency
 */
export function applyCountryChrome(chrome, trialDays = 0) {
  const taxLabelEl = document.getElementById("signupTaxIdLabel");
  const taxInput = document.getElementById("companyTaxId");
  const phoneInput = document.getElementById("contactPhone");
  const pricingHost = document.getElementById("signupPricingTableHost");
  const pricingSection = document.getElementById("signupPricingSection");
  const lockHint = document.getElementById("signupPricingLockHint");

  if (taxLabelEl) taxLabelEl.textContent = getTaxIdLabel(chrome.taxIdType);
  if (taxInput) {
    taxInput.value = "";
    taxInput.placeholder = getTaxIdPlaceholder(chrome.taxIdType);
    taxInput.maxLength = getTaxIdMaxLength(chrome.taxIdType);
    taxInput.inputMode = getTaxIdInputMode(chrome.taxIdType);
    taxInput.dataset.taxIdType = chrome.taxIdType;
  }
  if (phoneInput) {
    phoneInput.value = "";
    phoneInput.placeholder = getPhonePlaceholder(chrome.phoneRegion);
    phoneInput.dataset.phoneRegion = chrome.phoneRegion;
  }

  if (pricingHost) {
    pricingHost.classList.remove("hidden");
  }
  if (pricingSection) {
    const title = pricingSection.querySelector("h2");
    const subtitle = pricingSection.querySelector(".signup-pricing-subtitle");
    if (title) title.textContent = "Escolha seu plano";
    if (subtitle) {
      subtitle.textContent =
        trialDays > 0
          ? `Todos os planos incluem ${trialDays} dias grátis. Escolha o plano depois de confirmar os dados da empresa.`
          : "Compare preços e finalize o pagamento após confirmar os dados da empresa.";
    }
  }
  if (lockHint) {
    lockHint.textContent =
      "É rápido: validamos o documento fiscal automaticamente e em seguida você seleciona o plano.";
  }

  updateHowItWorksCopy(chrome.country, trialDays);

  const submitButton = document.getElementById("signupSubmitButton");
  const submitLabel = submitButton?.querySelector("[data-signup-submit-label]");
  if (submitLabel) {
    submitLabel.textContent = "Confirmar dados e escolher plano";
  }
}

/**
 * Mark the submit button as completed after onboarding intent succeeds.
 * @param {HTMLButtonElement|null|undefined} submitButton
 * @param {number} [trialDays]
 */
export function markSubmitReadyForPlan(submitButton, trialDays = 0) {
  if (!submitButton) return;
  submitButton.disabled = true;
  submitButton.classList.remove("opacity-70", "cursor-wait");
  submitButton.removeAttribute("aria-busy");
  delete submitButton.dataset.originalHtml;
  delete submitButton.dataset.originalLabel;
  delete submitButton.dataset.loading;
  submitButton.innerHTML =
    trialDays > 0
      ? `<span>Pronto! Agora escolha seu plano abaixo para liberar seus ${trialDays} dias grátis.</span>`
      : "<span>Pronto! Agora escolha seu plano abaixo para concluir sua assinatura.</span>";
}
